import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Building2, Check, Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/cn';
import { ConsentBadge } from '../ConsentBadge';
import { emailService, type ContactFilters } from '../../../lib/email/emailService';
import type { EmailContactConListe, EmailLista } from '../../../types/email.types';
import type { EmailAutomation } from '../../../types/automazioni.types';

interface AggiungiContattiDialogProps {
  aperta: boolean;
  lista: EmailLista | null;
  /** Le sequenze accese che partono da questa lista: chi entra le riceve. */
  sequenze: EmailAutomation[];
  onChiudi: () => void;
  /** Mette in lista i contatti scelti. La finestra si chiude quando ha finito. */
  onConferma: (ids: string[]) => Promise<void>;
}

/** Quanti contatti si caricano alla volta: con "Mostra altri" si va avanti. */
const PER_VOLTA = 50;

/**
 * Si sceglie dalla rubrica chi mettere nella lista: si cerca, si spunta, si
 * conferma. Chi e' gia' nella lista si vede, ma non si puo' spuntare.
 */
export function AggiungiContattiDialog({
  aperta,
  lista,
  sequenze,
  onChiudi,
  onConferma,
}: AggiungiContattiDialogProps) {
  const [ricerca, setRicerca] = useState('');
  const [ricercaAttiva, setRicercaAttiva] = useState('');
  const [soloAttivi, setSoloAttivi] = useState(false);
  const [trovati, setTrovati] = useState<EmailContactConListe[]>([]);
  const [totale, setTotale] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [scelti, setScelti] = useState<Set<string>>(new Set());
  const [caricando, setCaricando] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const filtri = useMemo<ContactFilters>(
    () => ({ search: ricercaAttiva, status: soloAttivi ? 'attivo' : 'tutti' }),
    [ricercaAttiva, soloAttivi]
  );

  // Ogni volta che si apre si riparte da zero: niente spunte rimaste da prima.
  const listaId = lista?.id;
  useEffect(() => {
    if (!aperta || !listaId) return;
    setRicerca('');
    setRicercaAttiva('');
    setSoloAttivi(false);
    setScelti(new Set());
    setErrore(null);
    // Via i risultati di prima: chi e' stato appena aggiunto sembrerebbe ancora fuori.
    setTrovati([]);
    setTotale(0);
  }, [aperta, listaId]);

  // La ricerca parte da sola dopo una pausa, come nella pagina Contatti.
  useEffect(() => {
    const t = setTimeout(() => setRicercaAttiva(ricerca), 350);
    return () => clearTimeout(t);
  }, [ricerca]);

  // Scrivendo veloce, una ricerca vecchia puo' rispondere dopo una nuova:
  // conta solo l'ultima partita.
  const ultimaRichiesta = useRef(0);

  const carica = useCallback(
    async (quale: number) => {
      const questa = ++ultimaRichiesta.current;
      setCaricando(true);
      setErrore(null);
      try {
        const risultato = await emailService.getContacts({ ...filtri, page: quale, pageSize: PER_VOLTA });
        if (questa !== ultimaRichiesta.current) return;
        setTrovati((prima) => (quale === 0 ? risultato.contatti : [...prima, ...risultato.contatti]));
        setTotale(risultato.totale);
        setPagina(quale);
      } catch (e) {
        if (questa === ultimaRichiesta.current) {
          setErrore(e instanceof Error ? e.message : 'Non riesco a cercare i contatti.');
        }
      } finally {
        if (questa === ultimaRichiesta.current) setCaricando(false);
      }
    },
    [filtri]
  );

  useEffect(() => {
    if (aperta) carica(0);
  }, [aperta, carica]);

  // Ogni contatto porta con se' tutte le sue liste: si vede subito se c'e' gia'.
  const dentro = (c: EmailContactConListe) => (lista ? c.liste.includes(lista.id) : false);

  const alterna = (id: string) =>
    setScelti((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(id)) dopo.delete(id);
      else dopo.add(id);
      return dopo;
    });

  const selezionabiliVisibili = trovati.filter((c) => !dentro(c));
  const tuttiVisibiliScelti =
    selezionabiliVisibili.length > 0 && selezionabiliVisibili.every((c) => scelti.has(c.id));

  const alternaVisibili = () =>
    setScelti((prima) => {
      const dopo = new Set(prima);
      if (tuttiVisibiliScelti) selezionabiliVisibili.forEach((c) => dopo.delete(c.id));
      else selezionabiliVisibili.forEach((c) => dopo.add(c.id));
      return dopo;
    });

  /** Anche quelli non ancora caricati: si tolgono solo quelli gia' nella lista. */
  const prendiTuttiITrovati = async () => {
    if (!lista) return;
    const questa = ++ultimaRichiesta.current;
    setCaricando(true);
    try {
      const [ids, membri] = await Promise.all([
        emailService.idsCorrispondenti(filtri),
        emailService.idsCorrispondenti({ listaId: lista.id }),
      ]);
      const giaDentro = new Set(membri);
      // Si aggiungono alle spunte fatte a mano con altre ricerche, non le sostituiscono.
      setScelti((prima) => new Set([...prima, ...ids.filter((id) => !giaDentro.has(id))]));
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non riesco a selezionarli tutti.');
    } finally {
      if (questa === ultimaRichiesta.current) setCaricando(false);
    }
  };

  const conferma = async () => {
    if (scelti.size === 0) return;
    setSalvando(true);
    try {
      await onConferma([...scelti]);
    } finally {
      setSalvando(false);
    }
  };

  const ancoraDaCaricare = trovati.length < totale;
  const quanti = scelti.size;

  return (
    <Dialog open={aperta} onOpenChange={(v) => !v && !salvando && onChiudi()}>
      <DialogContent className="max-w-2xl sm:h-[80vh] flex flex-col gap-0 overflow-y-hidden">
        <DialogHeader className="flex-shrink-0 pb-3">
          <DialogTitle>Aggiungi contatti a "{lista?.name ?? ''}"</DialogTitle>
          <DialogDescription>
            Cerca nella rubrica e spunta chi vuoi mettere nella lista. Chi c'e' gia' ha la spunta grigia.
          </DialogDescription>
        </DialogHeader>

        {sequenze.length > 0 && (
          <div className="flex-shrink-0 mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
            <span>
              Questa lista fa partire{' '}
              {sequenze.length === 1 ? 'la sequenza ' : 'le sequenze '}
              <strong>{sequenze.map((s) => `"${s.name}"`).join(', ')}</strong>: chi aggiungi ci entra
              subito e, se puo' ricevere, gli arrivano le sue email.
            </span>
          </div>
        )}

        <div className="flex-shrink-0 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Cerca per indirizzo, nome o azienda…"
              className="pl-9 h-10"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <label className="inline-flex items-center gap-2 text-neutral-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={soloAttivi}
                onChange={(e) => setSoloAttivi(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 accent-primary-600"
              />
              Solo chi puo' ricevere
            </label>
            <span className="text-neutral-500">
              {totale} {totale === 1 ? 'trovato' : 'trovati'}
            </span>
          </div>
          {trovati.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 pb-2 text-xs">
              <label className="inline-flex items-center gap-2 font-medium text-neutral-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={tuttiVisibiliScelti}
                  onChange={alternaVisibili}
                  disabled={selezionabiliVisibili.length === 0}
                  className="w-4 h-4 rounded border-gray-300 accent-primary-600"
                />
                Spunta quelli qui sotto
              </label>
              {ancoraDaCaricare && (
                <button
                  type="button"
                  onClick={prendiTuttiITrovati}
                  disabled={caricando}
                  className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
                >
                  Prendi tutti i {totale} trovati
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-[200px] overflow-y-auto -mx-1 px-1 py-2 space-y-1.5">
          {errore && <p className="text-xs text-red-600 px-1">{errore}</p>}
          {!caricando && trovati.length === 0 && !errore && (
            <p className="text-sm text-neutral-500 text-center py-10">
              {ricercaAttiva ? 'Nessun contatto per questa ricerca' : 'La rubrica e\' vuota'}
            </p>
          )}
          {trovati.map((c) => {
            const giaNellaLista = dentro(c);
            const spuntato = giaNellaLista || scelti.has(c.id);
            return (
              <label
                key={c.id}
                className={cn(
                  'flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors',
                  giaNellaLista
                    ? 'border-gray-100 bg-gray-50 cursor-default'
                    : spuntato
                      ? 'border-primary-300 bg-primary-50/50 cursor-pointer'
                      : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                )}
              >
                <input
                  type="checkbox"
                  checked={spuntato}
                  disabled={giaNellaLista}
                  onChange={() => alterna(c.id)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-primary-600 flex-shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 break-all">{c.email}</span>
                    <ConsentBadge status={c.status} />
                    {giaNellaLista && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-neutral-500">
                        <Check className="w-3 h-3" />
                        gia' nella lista
                      </span>
                    )}
                  </span>
                  {(c.full_name || c.company_name) && (
                    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-500">
                      {c.full_name && <span>{c.full_name}</span>}
                      {c.company_name && (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <Building2 className="w-3 h-3 flex-shrink-0 text-neutral-400" />
                          <span className="truncate max-w-[220px]">{c.company_name}</span>
                        </span>
                      )}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
          {caricando && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
            </div>
          )}
          {!caricando && ancoraDaCaricare && (
            <div className="flex justify-center pt-1">
              <Button variant="outline" size="sm" onClick={() => carica(pagina + 1)}>
                Mostra altri ({totale - trovati.length})
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-gray-100">
          <Button variant="outline" onClick={onChiudi} disabled={salvando}>
            Annulla
          </Button>
          <Button
            onClick={conferma}
            disabled={quanti === 0 || salvando}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {quanti === 0
              ? 'Spunta chi aggiungere'
              : `Aggiungi ${quanti} ${quanti === 1 ? 'contatto' : 'contatti'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
